import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { DatoGeograficoCementoVm } from '../../../../models/dato-geografico-cemento.vm';
import { DatoGeograficoCementoUpsertCommand } from '../../../../models/dato-geografico-cemento.model';

function nonEmptyTrimmedValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (typeof raw !== 'string' || raw.trim() === '') {
    return { required: true };
  }
  return null;
}

type DatoGeograficoForm = FormGroup<{
  codigoRegion: FormControl<string>;
  nombreRegion: FormControl<string>;
  codSapProvincia: FormControl<string>;
  nombreProvincia: FormControl<string>;
  codSapComuna: FormControl<string>;
  nombreComuna: FormControl<string>;
  zonaTransporte: FormControl<string>;
  activo: FormControl<boolean>;
}>;

type ControlKey = keyof DatoGeograficoForm['controls'];

@Component({
  selector: 'app-dato-geografico-cemento-detalle-modal',
  templateUrl: './dato-geografico-cemento-detalle-modal.component.html',
  styleUrls: ['./dato-geografico-cemento-detalle-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatoGeograficoCementoDetalleModalComponent implements OnChanges {
  @Input() item: DatoGeograficoCementoVm | null = null;
  @Input() loading = false;

  @Output() closeRequested = new EventEmitter<void>();
  @Output() saveRequested = new EventEmitter<DatoGeograficoCementoUpsertCommand>();
  @Output() deleteRequested = new EventEmitter<void>();

  readonly form: DatoGeograficoForm;

  constructor(private readonly fb: FormBuilder) {
    this.form = this.fb.group({
      codigoRegion: this.fb.nonNullable.control('', {
        validators: [nonEmptyTrimmedValidator],
      }),
      nombreRegion: this.fb.nonNullable.control('', {
        validators: [nonEmptyTrimmedValidator],
      }),
      codSapProvincia: this.fb.nonNullable.control('', {
        validators: [nonEmptyTrimmedValidator],
      }),
      nombreProvincia: this.fb.nonNullable.control('', {
        validators: [nonEmptyTrimmedValidator],
      }),
      codSapComuna: this.fb.nonNullable.control('', {
        validators: [nonEmptyTrimmedValidator],
      }),
      nombreComuna: this.fb.nonNullable.control('', {
        validators: [nonEmptyTrimmedValidator],
      }),
      zonaTransporte: this.fb.nonNullable.control('', {
        validators: [nonEmptyTrimmedValidator],
      }),
      activo: this.fb.nonNullable.control(true),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item']) {
      this.applyItemToForm();
    }
  }

  get isEditMode(): boolean {
    return (this.item?.id ?? 0) > 0;
  }

  get modalTitle(): string {
    return this.isEditMode ? 'Editar dato geográfico' : 'Crear dato geográfico';
  }

  fieldError(controlName: ControlKey): string {
    const control = this.form.controls[controlName];
    if (!control.touched || !control.invalid) {
      return '';
    }
    const errors = control.errors;
    if (errors?.['required']) {
      return 'Campo requerido';
    }
    return 'Revise el valor';
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saveRequested.emit({
      codigoRegion: this.form.controls.codigoRegion.getRawValue().trim(),
      nombreRegion: this.form.controls.nombreRegion.getRawValue().trim(),
      codSapProvincia: this.form.controls.codSapProvincia.getRawValue().trim(),
      nombreProvincia: this.form.controls.nombreProvincia.getRawValue().trim(),
      codSapComuna: this.form.controls.codSapComuna.getRawValue().trim(),
      nombreComuna: this.form.controls.nombreComuna.getRawValue().trim(),
      zonaTransporte: this.form.controls.zonaTransporte.getRawValue().trim(),
      activo: this.form.controls.activo.getRawValue(),
    });
  }

  onClose(): void {
    this.closeRequested.emit();
  }

  onDelete(): void {
    this.deleteRequested.emit();
  }

  private applyItemToForm(): void {
    if (!this.item) {
      this.form.reset({
        codigoRegion: '',
        nombreRegion: '',
        codSapProvincia: '',
        nombreProvincia: '',
        codSapComuna: '',
        nombreComuna: '',
        zonaTransporte: '',
        activo: true,
      });
      return;
    }

    this.form.reset({
      codigoRegion: this.item.codigoRegion,
      nombreRegion: this.item.nombreRegion,
      codSapProvincia: this.item.codSapProvincia,
      nombreProvincia: this.item.nombreProvincia,
      codSapComuna: this.item.codSapComuna,
      nombreComuna: this.item.nombreComuna,
      zonaTransporte: this.item.zonaTransporte,
      activo: this.item.activo,
    });
  }
}
